import React from 'react';
import { useEligibleForCodeFixTaskForGithubAndNode } from '../../../hooks/useEligibleForCodeFixTaskForGithubAndNode';
import { PleriClient } from '../client';
import { libraryItems } from './Library/libraryData';
import { useWebSocketStore } from '../../../store/websocketStore';
import { useUserProfile } from '../../../api/profile';
import { useLayoutStore } from '../../../store/layoutStore';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    chmln?: {
      identify: (userId: string, userData?: Record<string, unknown>) => void;
      show?: (experienceId: string) => void;
      data: {
        profile: {
          attributes: {
            [key: string]: unknown;
          };
        };
      };
      [key: string]: unknown;
    };
  }
}

const CODE_FIX_CUSTOM_MESSAGE_KEY =
  'code_fix_task_automation_message_2026_02_06';

export const PleriAnnouncements: React.FC = () => {
  const { t: tLibrary } = useTranslation('pleri/library');
  const {
    isEligible: eligibleForCodeFixTask,
    isLoading,
    nodeRepositories,
  } = useEligibleForCodeFixTaskForGithubAndNode();
  const wsStatus = useWebSocketStore((state) => state.status);
  const { data: userProfile } = useUserProfile();

  // Generate the personalized Chameleon message
  const messageToBePublished = React.useMemo(() => {
    if (!eligibleForCodeFixTask || nodeRepositories.length === 0) {
      return null;
    }

    // Extract first name from user profile
    const firstName = userProfile?.name?.split(' ')[0] || '';

    // Build repository scope text
    let repoScope = '';
    const repoCount = nodeRepositories.length;

    if (repoCount === 1) {
      repoScope = `in ${nodeRepositories[0].artifactName}`;
    } else if (repoCount === 2) {
      repoScope = `in ${nodeRepositories[0].artifactName}, ${nodeRepositories[1].artifactName}`;
    } else {
      // More than 2 repositories
      repoScope = `in a few repositories (${nodeRepositories[0].artifactName}, ${nodeRepositories[1].artifactName})`;
    }

    return `Hey${firstName ? ` ${firstName}` : ''}, I noticed some JavaScript dependency vulnerabilities ${repoScope}. Would you like to create a task so that I can automatically fix similar issues in the future?`;
  }, [eligibleForCodeFixTask, nodeRepositories, userProfile?.name]);
  const codeFixItem = libraryItems(tLibrary).find(
    (item) => item.id === 'fix-js-package-vulnerabilities',
  );
  const [hasExistingMessage, setHasExistingMessage] = React.useState(false);

  // Check if Chameleon is initialized and handle identify/show experience
  React.useEffect(() => {
    if (
      !eligibleForCodeFixTask ||
      !userProfile?.id ||
      !messageToBePublished ||
      !codeFixItem
    ) {
      return;
    }

    let checkCount = 0;
    let hasIdentified = false;
    const MAX_CHECKS = 60; // Check for up to 60 seconds (60 * 1000ms)
    const POLL_INTERVAL = 1000; // 1000ms

    const checkChameleon = setInterval(() => {
      checkCount++;
      if (checkCount >= MAX_CHECKS) {
        clearInterval(checkChameleon);
        return;
      }

      // Wait for chmln to be available
      if (window.chmln) {
        const existingMessage = window?.chmln?.data?.profile?.attributes?.[
          CODE_FIX_CUSTOM_MESSAGE_KEY
        ] as string | undefined;
        // Only set the message if it doesn't already exist
        if (!hasIdentified && !existingMessage) {
          window.chmln.identify(userProfile.id, {
            [CODE_FIX_CUSTOM_MESSAGE_KEY]: messageToBePublished,
          });
          hasIdentified = true;
        } else if (existingMessage) {
          setHasExistingMessage(true);
          clearInterval(checkChameleon);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(checkChameleon);
  }, [
    eligibleForCodeFixTask,
    userProfile?.id,
    messageToBePublished,
    codeFixItem,
  ]);

  // Don't show anything while loading status
  if (isLoading) {
    return null;
  }

  if (wsStatus !== 'OPEN') {
    return null;
  }

  const handleCodeFixTaskClick = () => {
    if (codeFixItem) {
      useLayoutStore.getState().setMiniMode(false);
      PleriClient.updateChatInput({
        prompt: codeFixItem.prompt,
        useExistingConversation: false,
        autoSubmit: codeFixItem.autoSubmit,
        metrics: {
          eventLocation: 'pleri-announcements',
          eventName: 'codeFixTaskClick',
          eventType: codeFixItem.autoSubmit ? 'autoSubmitQuery' : '',
          eventValue: codeFixItem.id,
          eventDetails: {
            category: codeFixItem.category,
            title: codeFixItem.title,
            tags: codeFixItem.tags,
          },
        },
      });
    }
  };

  return (
    <>
      {eligibleForCodeFixTask && codeFixItem && hasExistingMessage && (
        <button
          onClick={handleCodeFixTaskClick}
          className="invisible absolute"
          id="chmln-code-fix-task-eligible-button"
        />
      )}
    </>
  );
};
